import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { useAuth } from "./AuthContext";

interface NavigationHistoryEntry {
  path: string;
  timestamp: number;
}

interface NavigationHistoryContextProps {
  history: NavigationHistoryEntry[];
  canGoBack: boolean;
  goBack: () => void;
  clearHistory: () => void;
  getCurrentPath: () => string;
}

const NavigationHistoryContext = createContext<
  NavigationHistoryContextProps | undefined
>(undefined);

// Paths to exclude from navigation history
const EXCLUDED_PATHS = new Set([
  "/login",
  "/signup",
  "/forgot-password",
  "/forgotpassword",
  "/403",
  "/",
]);

// Paths whose query-string state represents in-page navigation (e.g. tab
// indices) rather than filters. For these, an explicit PUSH navigation that
// only changes the query string is recorded as a separate history entry so
// the in-app back button mirrors browser back behavior. REPLACE navigations
// (used for filter writes) still collapse, preserving existing UX elsewhere.
const QUERY_AS_NAVIGATION_PATHS = new Set([
  "/scoring-config",
  "/scoring-defaults",
]);

// Maximum number of history entries to maintain
const MAX_HISTORY_SIZE = 10;

// SessionStorage key for navigation history
const HISTORY_STORAGE_KEY = "navigationHistory";

interface NavigationHistoryProviderProps {
  children: ReactNode;
}

export const NavigationHistoryProvider: React.FC<
  NavigationHistoryProviderProps
> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const { isAuthenticated } = useAuth();

  const [history, setHistory] = useState<NavigationHistoryEntry[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load history from sessionStorage on mount and ensure current page is at top
  useEffect(() => {
    if (!isInitialized) {
      try {
        const storedHistory = sessionStorage.getItem(HISTORY_STORAGE_KEY);
        const { pathname, search } = location;
        const currentPath = `${pathname}${search || ""}`;

        if (storedHistory && !EXCLUDED_PATHS.has(pathname)) {
          const parsedHistory = JSON.parse(storedHistory);
          if (Array.isArray(parsedHistory) && parsedHistory.length > 0) {
            // Always restore the full history from sessionStorage
            setHistory(parsedHistory);
          } else {
            // Invalid stored history, create new entry for current page
            const currentEntry: NavigationHistoryEntry = {
              path: currentPath,
              timestamp: Date.now(),
            };
            setHistory([currentEntry]);
          }
        } else if (!EXCLUDED_PATHS.has(pathname)) {
          // No stored history, create new entry for current page
          const currentEntry: NavigationHistoryEntry = {
            path: currentPath,
            timestamp: Date.now(),
          };
          setHistory([currentEntry]);
        }
        setIsInitialized(true);
      } catch (error) {
        console.warn(
          "Failed to load navigation history from sessionStorage:",
          error,
        );
        setIsInitialized(true);
      }
    }
  }, [isInitialized, location]);

  // Add current page to history after initialization (allowing duplicates)
  useEffect(() => {
    if (isInitialized) {
      const { pathname, search } = location;
      const currentPath = `${pathname}${search || ""}`;

      if (!EXCLUDED_PATHS.has(pathname)) {
        setHistory((prevHistory) => {
          // If no history exists, create initial entry
          if (prevHistory.length === 0) {
            const currentEntry: NavigationHistoryEntry = {
              path: currentPath,
              timestamp: Date.now(),
            };
            return [currentEntry];
          }

          // Check if current page is already at the top
          if (prevHistory[0].path !== currentPath) {
            // Check if only query parameters changed (same pathname)
            const currentPathname = pathname;
            const previousPath = prevHistory[0].path;
            const previousPathname = previousPath.split("?")[0];

            if (currentPathname === previousPathname) {
              // For paths that treat query state as in-page navigation, a
              // PUSH that changes only the query string still records a new
              // history entry so the in-app back button matches the browser
              // back button. REPLACE/POP keep the existing collapse behavior.
              if (
                QUERY_AS_NAVIGATION_PATHS.has(currentPathname) &&
                navigationType === "PUSH"
              ) {
                const currentEntry: NavigationHistoryEntry = {
                  path: currentPath,
                  timestamp: Date.now(),
                };
                return [currentEntry, ...prevHistory].slice(
                  0,
                  MAX_HISTORY_SIZE,
                );
              }
              // Only query params changed, update the current entry instead of adding new one
              const updatedEntry: NavigationHistoryEntry = {
                path: currentPath,
                timestamp: Date.now(),
              };
              return [updatedEntry, ...prevHistory.slice(1)];
            } else {
              // Different pathname, create new entry for current page
              const currentEntry: NavigationHistoryEntry = {
                path: currentPath,
                timestamp: Date.now(),
              };

              // Add current page to the top, keeping all previous entries including duplicates
              const newHistory = [currentEntry, ...prevHistory].slice(
                0,
                MAX_HISTORY_SIZE,
              );
              return newHistory;
            }
          }

          return prevHistory;
        });
      }
    }
  }, [location, isInitialized, navigationType]);

  // Save history to sessionStorage whenever it changes
  useEffect(() => {
    // Only save if we have history and are initialized
    if (isInitialized && history.length > 0) {
      try {
        sessionStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history));
      } catch (error) {
        console.warn(
          "Failed to save navigation history to sessionStorage:",
          error,
        );
      }
    }
  }, [history, isInitialized]);

  // Preserve navigation history across authentication state changes
  useEffect(() => {
    // When authentication state changes, ensure we don't lose navigation history
    // This is particularly important during page refresh when auth state might change
    if (isInitialized && isAuthenticated) {
      const storedHistory = sessionStorage.getItem(HISTORY_STORAGE_KEY);
      if (storedHistory && history.length === 0) {
        try {
          const parsedHistory = JSON.parse(storedHistory);
          if (Array.isArray(parsedHistory) && parsedHistory.length > 0) {
            setHistory(parsedHistory);
          }
        } catch (error) {
          console.warn(
            "Failed to restore navigation history after auth state change:",
            error,
          );
        }
      }
    }
  }, [isAuthenticated, isInitialized, history.length]);

  // Ensure navigation history is saved before page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (history.length > 0) {
        try {
          sessionStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history));
        } catch (error) {
          console.warn(
            "Failed to save navigation history before unload:",
            error,
          );
        }
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [history]);

  const canGoBack = history.length > 1;

  const goBack = useCallback(() => {
    if (canGoBack) {
      // Get the previous path (index 1, since index 0 is current)
      const previousEntry = history[1];
      if (previousEntry) {
        // Remove the current entry from history before navigating
        setHistory((prevHistory) => prevHistory.slice(1));
        navigate(previousEntry.path);
      }
    } else {
      // Fallback to browser back if no history available
      if (window.history.length > 1) {
        window.history.back();
      }
    }
  }, [history, canGoBack, navigate]);

  const clearHistory = useCallback(() => {
    setHistory([]);
    try {
      sessionStorage.removeItem(HISTORY_STORAGE_KEY);
    } catch (error) {
      console.warn(
        "Failed to clear navigation history from sessionStorage:",
        error,
      );
    }
  }, []);

  const getCurrentPath = useCallback(() => {
    const { pathname, search } = location;
    return `${pathname}${search || ""}`;
  }, [location]);

  const contextValue: NavigationHistoryContextProps = {
    history,
    canGoBack,
    goBack,
    clearHistory,
    getCurrentPath,
  };

  return (
    <NavigationHistoryContext.Provider value={contextValue}>
      {children}
    </NavigationHistoryContext.Provider>
  );
};

export const useNavigationHistory = (): NavigationHistoryContextProps => {
  const context = useContext(NavigationHistoryContext);
  if (!context) {
    throw new Error(
      "useNavigationHistory must be used within a NavigationHistoryProvider",
    );
  }
  return context;
};

export default NavigationHistoryContext;
